import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faBolt, faCopy, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import List from "../../../components/List/List"
import { EPaymentProcessingSeverityType } from "../../../Enum/EPaymentProcessingSeverityType"
import { AdminDashboard } from "../../../Objects/AdminDashboard/AdminDashboard"
import { AdminSubscriptionCase } from "../../../Objects/AdminDashboard/AdminSubscriptionCase"

interface SubscriptionsSectionProps {
  dashboard: AdminDashboard
  onManualActivate: (subscriptionId: number) => void
  onShowMoreOptions: (companyId: number) => void
  onEditSubscription: (subscription: AdminSubscriptionCase) => void
  onRefresh: () => void
}

interface SubscriptionCardActionProps {
  className: string
  icon: IconProp
  label: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  descriptionKey: string
}

const SUBSCRIPTION_FILTERS = [
  "admin_dashboard.subscription_monitoring.filter_real",
  "admin_dashboard.subscription_monitoring.filter_demo",
  "admin_dashboard.subscription_monitoring.filter_all",
] as const

const severityClassMap: Record<EPaymentProcessingSeverityType, string> = {
  [EPaymentProcessingSeverityType.None]: "bg-slate-100 text-slate-700",
  [EPaymentProcessingSeverityType.Recent]: "bg-emerald-100 text-emerald-700",
  [EPaymentProcessingSeverityType.Acceptable]: "bg-blue-100 text-blue-700",
  [EPaymentProcessingSeverityType.Delayed]: "bg-amber-100 text-amber-700",
  [EPaymentProcessingSeverityType.Critical]: "bg-red-100 text-red-700",
}

const formatText = (value?: string | null) => {
  if (!value?.trim()) return "-"

  return value
}

const formatDate = (value?: string) => {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("pt-BR")
}

const diffInCalendarDays = (value?: string) => {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  const today = new Date()

  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()

  const createdStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

  return Math.floor((todayStart - createdStart) / 86400000)
}

const SubscriptionCardAction: React.FC<SubscriptionCardActionProps> = ({
  className,
  icon,
  label,
  onClick,
  descriptionKey,
}) => (
  <button
    className={`inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${className}`}
    onClick={onClick}
    data-interaction-description-key={descriptionKey}
  >
    <FontAwesomeIcon icon={icon} />
    <span>{label}</span>
  </button>
)

const CopyContactButton: React.FC<{ label: string; value: string; descriptionKey: string }> = ({
  label,
  value,
  descriptionKey,
}) => (
  <button
    className="inline-flex min-h-[28px] items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-black text-slate-700 transition-colors hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
    disabled={!value.trim()}
    title={label}
    onClick={(event) => {
      event.stopPropagation()

      if (value.trim()) {
        navigator.clipboard.writeText(value)
      }
    }}
    data-interaction-description-key={descriptionKey}
  >
    <FontAwesomeIcon icon={faCopy} />
    <span>{label}</span>
  </button>
)

const ContactItem: React.FC<{ label: string; value: string; copyLabel: string; descriptionKey: string }> = ({
  label,
  value,
  copyLabel,
  descriptionKey,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2">
    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-wide text-slate-500">{label}</p>

      <p className="mt-0.5 break-words text-sm font-bold text-slate-800">{formatText(value)}</p>
    </div>

    <CopyContactButton label={copyLabel} value={value} descriptionKey={descriptionKey} />
  </div>
)

const MetricItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
    <p className="text-[10px] font-black uppercase tracking-wide text-slate-500">{label}</p>

    <p className="mt-1 break-words text-sm font-bold text-slate-800">{value}</p>
  </div>
)

export const SubscriptionsSection: React.FC<SubscriptionsSectionProps> = ({
  dashboard,
  onManualActivate,
  onShowMoreOptions,
  onEditSubscription,
  onRefresh,
}) => {
  const { t } = useTranslation("frontend")

  const dropdownFilters = SUBSCRIPTION_FILTERS.map((key) => t(key))

  const getStatusLabel = (item: AdminSubscriptionCase) =>
    item.subscriptionStatus || item.lastPaymentStatus || t("admin_dashboard.subscription_monitoring.status_empty")

  const getPlanLabel = (item: AdminSubscriptionCase) =>
    item.isTestOrDemoCompany ? "-" : item.planType.toEAccountPlansTypeToString()

  const getCommissionLabel = (item: AdminSubscriptionCase) => {
    if (item.isTestOrDemoCompany) return "-"

    if (!item.isCommissionEligible) return t("admin_dashboard.subscription_monitoring.commission_not_eligible")

    if (!item.commissionPercentage) return t("admin_dashboard.subscription_monitoring.commission_eligible")

    return `${item.commissionPercentage}%`
  }

  const getSeverityLabel = (severity: EPaymentProcessingSeverityType) => {
    const severityKeyMap: Record<EPaymentProcessingSeverityType, string> = {
      [EPaymentProcessingSeverityType.None]: "none",
      [EPaymentProcessingSeverityType.Recent]: "recent",
      [EPaymentProcessingSeverityType.Acceptable]: "acceptable",
      [EPaymentProcessingSeverityType.Delayed]: "delayed",
      [EPaymentProcessingSeverityType.Critical]: "critical",
    }

    return t(`admin_dashboard.subscription_monitoring.severity_${severityKeyMap[severity] ?? "none"}`)
  }

  const getNewSubscriptionBadge = (item: AdminSubscriptionCase) => {
    const days = diffInCalendarDays(item.createdAt)

    if (days === null || days < 0 || days > 3) return null

    if (days === 0) return t("admin_dashboard.subscription_monitoring.created_today")

    if (days === 1) return t("admin_dashboard.subscription_monitoring.created_yesterday")

    return t("admin_dashboard.subscription_monitoring.created_last_3_days")
  }

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="p-2">
        <List
          messageCount={t("admin_dashboard.subscription_monitoring.count")}
          messageEmpty={t("admin_dashboard.subscription_monitoring.empty")}
          nameFindSearch={t("admin_dashboard.subscription_monitoring.search")}
          nameDropdown={dropdownFilters}
          data={dashboard.subscriptionCases.map((item, index) => {
            const statusLabel = getStatusLabel(item)
            const originLabel = item.isTestOrDemoCompany
              ? "-"
              : item.commercialOriginType.toECommercialOriginTypeString()
            const severityClass =
              severityClassMap[item.processingSeverity] ?? severityClassMap[EPaymentProcessingSeverityType.None]
            const severityLabel = getSeverityLabel(item.processingSeverity)
            const newSubscriptionBadge = getNewSubscriptionBadge(item)
            const cardToneClass = newSubscriptionBadge
              ? "rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3"
              : "rounded-xl bg-white p-3"

            return {
              id: item.subscriptionId,
              index,
              title: item.companyName,
              linkImage: "",
              isDisable: item.isTestOrDemoCompany,
              dataFilter: [
                item.companyName,
                item.ownerPhone,
                item.ownerEmail,
                statusLabel,
                originLabel,
                item.salesRepresentativeName,
                item.commercialReferralCode,
                item.providerName,
                severityLabel,
                newSubscriptionBadge,
                item.isTestOrDemoCompany
                  ? t("admin_dashboard.subscription_monitoring.filter_demo")
                  : t("admin_dashboard.subscription_monitoring.filter_real"),
              ].filter((value): value is string => Boolean(value && value.length > 0)),
              children: (
                <div className={cardToneClass}>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="break-words text-base font-black text-slate-900">{item.companyName}</p>
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          {t("admin_dashboard.subscription_monitoring.created_at")}: {formatDate(item.createdAt)}
                        </p>
                      </div>

                      <div className="flex flex-wrap justify-end gap-2">
                        {newSubscriptionBadge && (
                          <span className="rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-black uppercase text-white">
                            {newSubscriptionBadge}
                          </span>
                        )}

                        {item.isTestOrDemoCompany && (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase text-slate-600">
                            {t("admin_dashboard.subscription_details.test_or_demo_badge")}
                          </span>
                        )}

                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-black uppercase ${severityClass}`}>
                          {item.isTestOrDemoCompany
                            ? t("admin_dashboard.subscription_monitoring.demo_status")
                            : severityLabel}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      <ContactItem
                        label={t("admin_dashboard.subscription_monitoring.master_phone")}
                        value={item.ownerPhone}
                        copyLabel={t("admin_dashboard.subscription_monitoring.copy_phone")}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.sections.subscriptionsSection.copy.masterPhone"
                      />

                      <ContactItem
                        label={t("admin_dashboard.subscription_monitoring.master_email")}
                        value={item.ownerEmail}
                        copyLabel={t("admin_dashboard.subscription_monitoring.copy_email")}
                        descriptionKey="interactionDescriptions:pages.adminDashboard.sections.subscriptionsSection.copy.masterEmail"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <MetricItem
                        label={t("admin_dashboard.subscription_monitoring.plan")}
                        value={getPlanLabel(item)}
                      />

                      <MetricItem label={t("admin_dashboard.subscription_monitoring.status")} value={statusLabel} />

                      <MetricItem
                        label={t("admin_dashboard.subscription_monitoring.monthly_amount")}
                        value={item.isTestOrDemoCompany ? "-" : item.currentChargeAmount.toMoney()}
                      />

                      <MetricItem
                        label={t("admin_dashboard.subscription_monitoring.next_payment")}
                        value={item.isTestOrDemoCompany ? "-" : formatDate(item.nextPaymentDate)}
                      />

                      <MetricItem
                        label={t("admin_dashboard.subscription_monitoring.last_payment")}
                        value={item.isTestOrDemoCompany ? "-" : formatDate(item.lastPaymentDate)}
                      />

                      <MetricItem
                        label={t("admin_dashboard.subscription_monitoring.support")}
                        value={item.isTestOrDemoCompany ? "-" : item.supportRequestsCount}
                      />
                    </div>

                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-slate-700">
                      <div className="flex flex-col gap-1">
                        <p>
                          <strong>{t("admin_dashboard.subscription_monitoring.origin")}:</strong> {originLabel}
                        </p>

                        {!item.isTestOrDemoCompany && item.salesRepresentativeName && (
                          <p>
                            <strong>{t("admin_dashboard.subscription_monitoring.sales_representative")}:</strong>{" "}
                            {item.salesRepresentativeName}
                          </p>
                        )}

                        {!item.isTestOrDemoCompany && item.commercialReferralCode && (
                          <p>
                            <strong>{t("admin_dashboard.subscription_monitoring.referral_code")}:</strong>{" "}
                            {item.commercialReferralCode}
                          </p>
                        )}

                        <p>
                          <strong>{t("admin_dashboard.subscription_monitoring.commission")}:</strong>{" "}
                          {getCommissionLabel(item)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                      {!item.isTestOrDemoCompany && (
                        <SubscriptionCardAction
                          className="border border-blue-500 bg-blue-600 text-white hover:bg-blue-700"
                          icon={faPenToSquare}
                          label={t("admin_dashboard.subscription_edit.action")}
                          onClick={(event) => {
                            event.stopPropagation()
                            onEditSubscription(item)
                          }}
                          descriptionKey="interactionDescriptions:pages.adminDashboard.sections.subscriptionsSection.action.editSubscription"
                        />
                      )}

                      {item.canManualActivate && (
                        <SubscriptionCardAction
                          className="border border-orange-500 bg-orange-600 text-white hover:bg-orange-700"
                          icon={faBolt}
                          label={t("admin_dashboard.manual_activate")}
                          onClick={(event) => {
                            event.stopPropagation()
                            onManualActivate(item.subscriptionId)
                          }}
                          descriptionKey="interactionDescriptions:pages.adminDashboard.sections.subscriptionsSection.action.manualActivation"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ),
            }
          })}
          handleRefreshButton={onRefresh}
          handleOnClick={(id) => {
            const item = dashboard.subscriptionCases.find((subscription) => subscription.subscriptionId === id)

            if (item) {
              onShowMoreOptions(item.companyId)
            }
          }}
        />
      </div>
    </div>
  )
}
